import ErrorUtils from 'utils/error-utils';
/**
 * This is a singleton that is used to intercept axios requests and attach
 * Auth0 access token to the Authorization header
 *
 * @see {@link https://auth0.github.io/auth0-react/interfaces/auth0_context.auth0contextinterface.html#getaccesstokensilently | Auth0 getAccessTokenSilently()}
 * @see {@link https://spothero.github.io/fe-utils/module-APIUtils.html#.attachInterceptor | FE Utils attachInterceptor()}
 */
export class Auth0ServiceInterceptor {
    /**
     * Sets the token generator function that will later be called in the interceptor
     *
     * @param {Function} tokenGenerator - an async function that gets an access token (getAccessTokenSilently())
     * @returns {Promise} which resolves to this Auth0ServiceInterceptor
     */
    setTokenGenerator(tokenGenerator) {
        return new Promise(resolve => {
            this.getToken = tokenGenerator;

            resolve(this);
        });
    }
    /**
     * Resets the token generator function that will later be called in the interceptor. To undo setTokenGenerator after a logout
     *
     * @returns {Promise} which resolves to this Auth0ServiceInterceptor
     */
    resetTokenGenerator() {
        this.getToken = () => {
            return new Promise(resolve => {
                resolve('');
            });
        };
    }
    /**
     * Get the access token.
     * A default token generator function, which is meant to be replaced in setTokenGenerator
     *
     * @see {@link https://auth0.github.io/auth0-react/interfaces/index.gettokensilentlyoptions.html | Auth0 GetTokenSilentlyOptions}
     * @param {object} [tokenOptions] - options such as audience and scope. If unspecified, the default options will be used.
     * @returns {Promise} which resolves to a string
     */
    getToken(tokenOptions) {
        return new Promise(resolve => {
            resolve('');
        });
    }
    /**
     * Adds an interceptor to an axios instance that gets a token using the provided options,
     * then attaches the token information in the Authorization header.
     *
     * Different APIs (e.g. V1/Monolith and V2/Craig) will have different audience/scope.
     * Within the category of V1 APIs, multiple axios instances have been created to handle
     * specific needs.
     *
     * If no tokenOptions are specified, the default options (see auth0 initialization) will be used.
     *
     * @param {object} axiosInstance - a given axios instance to attach the interceptor to
     * @param {object} [tokenOptions] - See getToken() for usage
     */
    attachInterceptorWithToken(axiosInstance, tokenOptions) {
        axiosInstance.attachInterceptor({
            method: 'request',
            fulfilled: async config => {
                const token = await this.getToken(tokenOptions);

                if (token) {
                    return {
                        ...config,
                        headers: {
                            ...config.headers,
                            Authorization: `Bearer ${token}`,
                        },
                    };
                }

                return config;
            },
            rejected: err => ErrorUtils.transformAndReject(err),
        });
    }
}

const instance = new Auth0ServiceInterceptor();

export default instance;
